/**
 * Integration Services
 *
 * Services for external integrations including Microsoft Teams,
 * webhooks, email notifications, calendar export, and LinkedIn.
 */

import { apiClient } from '../../lib/awsConfig';
import type {
  ApiResponse,
  IntegrationConfig,
  TeamsIntegrationConfig,
  EmailIntegrationConfig,
  WebhookIntegrationConfig,
  LinkedInIntegrationConfig,
  LinkedInPost,
  LinkedInPostRequest,
  LinkedInPostMetrics,
  LinkedInOrganization,
  LinkedInAuthor,
  LinkedInTokenResponse,
  LinkedInAnalyticsSummary,
  ContentSeed,
  ContentOutput,
} from '../../types';

// =============================================================================
// MICROSOFT TEAMS INTEGRATION SERVICE
// =============================================================================

/**
 * Teams Adaptive Card builder helpers
 */
const teamsCardBuilder = {
  /**
   * Create a basic notification card
   */
  notification(
    title: string,
    message: string,
    color: string = '#0078D4',
    facts?: Array<{ title: string; value: string }>
  ): Record<string, unknown> {
    const card: Record<string, unknown> = {
      type: 'AdaptiveCard',
      $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
      version: '1.4',
      body: [
        {
          type: 'TextBlock',
          text: title,
          weight: 'Bolder',
          size: 'Medium',
          color: 'Accent',
        },
        {
          type: 'TextBlock',
          text: message,
          wrap: true,
        },
      ],
    };

    if (facts && facts.length > 0) {
      (card.body as unknown[]).push({
        type: 'FactSet',
        facts: facts.map(f => ({ title: f.title, value: f.value })),
      });
    }

    return {
      type: 'message',
      attachments: [
        {
          contentType: 'application/vnd.microsoft.card.adaptive',
          contentUrl: null,
          content: card,
        },
      ],
    };
  },

  /**
   * Create an actionable card with buttons
   */
  actionable(
    title: string,
    message: string,
    actions: Array<{
      title: string;
      type: 'openUrl' | 'submit';
      url?: string;
      data?: Record<string, unknown>;
    }>,
    facts?: Array<{ title: string; value: string }>
  ): Record<string, unknown> {
    const cardActions = actions.map(action => {
      if (action.type === 'openUrl') {
        return {
          type: 'Action.OpenUrl',
          title: action.title,
          url: action.url,
        };
      }
      return {
        type: 'Action.Submit',
        title: action.title,
        data: action.data,
      };
    });

    const card: Record<string, unknown> = {
      type: 'AdaptiveCard',
      $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
      version: '1.4',
      body: [
        {
          type: 'TextBlock',
          text: title,
          weight: 'Bolder',
          size: 'Medium',
          color: 'Accent',
        },
        {
          type: 'TextBlock',
          text: message,
          wrap: true,
        },
      ],
      actions: cardActions,
    };

    if (facts && facts.length > 0) {
      (card.body as unknown[]).push({
        type: 'FactSet',
        facts: facts.map(f => ({ title: f.title, value: f.value })),
      });
    }

    return {
      type: 'message',
      attachments: [
        {
          contentType: 'application/vnd.microsoft.card.adaptive',
          contentUrl: null,
          content: card,
        },
      ],
    };
  },

  /**
   * Create an approval request card
   */
  approvalRequest(
    title: string,
    contentTitle: string,
    requestedBy: string,
    contentUrl: string,
    approvalId: string,
    callbackUrl: string
  ): Record<string, unknown> {
    return {
      type: 'message',
      attachments: [
        {
          contentType: 'application/vnd.microsoft.card.adaptive',
          contentUrl: null,
          content: {
            type: 'AdaptiveCard',
            $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
            version: '1.4',
            body: [
              {
                type: 'TextBlock',
                text: '🔔 Approval Request',
                weight: 'Bolder',
                size: 'Large',
                color: 'Accent',
              },
              {
                type: 'TextBlock',
                text: title,
                weight: 'Bolder',
                size: 'Medium',
                wrap: true,
              },
              {
                type: 'FactSet',
                facts: [
                  { title: 'Content', value: contentTitle },
                  { title: 'Requested by', value: requestedBy },
                ],
              },
              {
                type: 'TextBlock',
                text: 'Add a comment (optional):',
                size: 'Small',
              },
              {
                type: 'Input.Text',
                id: 'comment',
                placeholder: 'Enter your comment...',
                isMultiline: true,
              },
            ],
            actions: [
              {
                type: 'Action.Submit',
                title: '✅ Approve',
                style: 'positive',
                data: {
                  action: 'approve',
                  approvalId,
                  callbackUrl,
                },
              },
              {
                type: 'Action.Submit',
                title: '❌ Reject',
                style: 'destructive',
                data: {
                  action: 'reject',
                  approvalId,
                  callbackUrl,
                },
              },
              {
                type: 'Action.OpenUrl',
                title: '👁️ View Content',
                url: contentUrl,
              },
            ],
          },
        },
      ],
    };
  },
};

export const teamsService = {
  /**
   * Send a message to Teams via backend webhook proxy.
   * This avoids CORS issues - Teams webhooks don't support browser preflight requests.
   */
  async sendMessage(
    webhookUrl: string,
    payload: Record<string, unknown>
  ): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        '/teams/webhook',
        { webhookUrl, payload }
      );

      if (response.error) {
        throw response.error;
      }

      return { data: response.data?.success ?? false, error: null };
    } catch (error) {
      console.error('[Teams] Message failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Test Teams webhook connectivity via backend proxy
   */
  async testWebhook(webhookUrl: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        '/teams/test',
        { webhookUrl }
      );
      return response;
    } catch (error) {
      console.error('[Teams] Test failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Send a notification card
   */
  async sendNotification(
    webhookUrl: string,
    title: string,
    message: string,
    color?: string,
    facts?: Array<{ title: string; value: string }>
  ): Promise<ApiResponse<boolean>> {
    const card = teamsCardBuilder.notification(title, message, color, facts);
    return this.sendMessage(webhookUrl, card);
  },

  /**
   * Notify about content status change
   */
  async notifyStatusChange(
    config: TeamsIntegrationConfig,
    content: ContentSeed | ContentOutput,
    oldStatus: string,
    newStatus: string,
    changedBy: string,
    contentUrl?: string
  ): Promise<ApiResponse<boolean>> {
    if (!config.notifyOnStatusChange) {
      return { data: false, error: null };
    }

    const _statusColor = newStatus === 'Published' ? '#22c55e' : '#0078D4';
    void _statusColor; // Reserved for future card theming
    const actions = contentUrl
      ? [{ title: 'View Content', type: 'openUrl' as const, url: contentUrl }]
      : [];

    const card = teamsCardBuilder.actionable(
      '📝 Content Status Updated',
      `"${content.title}" status has been updated`,
      actions,
      [
        { title: 'Previous Status', value: oldStatus },
        { title: 'New Status', value: newStatus },
        { title: 'Changed by', value: changedBy },
      ]
    );

    return this.sendMessage(config.webhookUrl, card);
  },

  /**
   * Notify about user mention
   */
  async notifyMention(
    config: TeamsIntegrationConfig,
    mentionedUser: string,
    mentionedBy: string,
    contentTitle: string,
    noteText: string,
    contentUrl?: string
  ): Promise<ApiResponse<boolean>> {
    if (!config.notifyOnMention) {
      return { data: false, error: null };
    }

    const actions = contentUrl
      ? [{ title: 'View Content', type: 'openUrl' as const, url: contentUrl }]
      : [];

    const card = teamsCardBuilder.actionable(
      '💬 You were mentioned',
      `${mentionedBy} mentioned you: "${noteText}"`,
      actions,
      [
        { title: 'Content', value: contentTitle },
        { title: 'Mentioned by', value: mentionedBy },
      ]
    );

    return this.sendMessage(config.webhookUrl, card);
  },

  /**
   * Notify about content assignment
   */
  async notifyAssignment(
    config: TeamsIntegrationConfig,
    content: ContentOutput,
    assignedTo: string,
    assignedBy: string,
    contentUrl?: string
  ): Promise<ApiResponse<boolean>> {
    if (!config.notifyOnAssignment) {
      return { data: false, error: null };
    }

    const actions = contentUrl
      ? [{ title: 'View Content', type: 'openUrl' as const, url: contentUrl }]
      : [];

    const card = teamsCardBuilder.actionable(
      '📋 Content Assigned to You',
      `You have been assigned to work on "${content.title}"`,
      actions,
      [
        { title: 'Content Type', value: content.contentType },
        { title: 'Due Date', value: content.dueDate || 'Not set' },
        { title: 'Assigned by', value: assignedBy },
      ]
    );

    return this.sendMessage(config.webhookUrl, card);
  },

  /**
   * Send approval request with quick actions
   */
  async sendApprovalRequest(
    config: TeamsIntegrationConfig,
    contentTitle: string,
    requestedBy: string,
    approvalId: string,
    contentUrl: string
  ): Promise<ApiResponse<boolean>> {
    if (!config.notifyOnApproval || !config.enableQuickActions) {
      return { data: false, error: null };
    }

    const callbackUrl = config.actionCallbackUrl || '';
    const card = teamsCardBuilder.approvalRequest(
      'Content Ready for Review',
      contentTitle,
      requestedBy,
      contentUrl,
      approvalId,
      callbackUrl
    );

    return this.sendMessage(config.webhookUrl, card);
  },

  /**
   * Send actionable card with custom buttons
   */
  async sendActionableCard(
    config: TeamsIntegrationConfig,
    title: string,
    message: string,
    actions: Array<{
      title: string;
      type: 'openUrl' | 'submit';
      url?: string;
      data?: Record<string, unknown>;
    }>,
    facts?: Array<{ title: string; value: string }>
  ): Promise<ApiResponse<boolean>> {
    if (!config.enableQuickActions && actions.some(a => a.type === 'submit')) {
      // Filter out submit actions if quick actions are disabled
      actions = actions.filter(a => a.type === 'openUrl');
    }

    const card = teamsCardBuilder.actionable(title, message, actions, facts);
    return this.sendMessage(config.webhookUrl, card);
  },

  /**
   * Handle incoming action callback from Teams
   */
  async handleActionCallback(
    payload: {
      action: string;
      approvalId?: string;
      comment?: string;
      [key: string]: unknown;
    }
  ): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        '/integrations/teams/callback',
        payload
      );
      return response;
    } catch (error) {
      console.error('[Teams] Action callback failed:', error);
      return { data: null, error: error as Error };
    }
  },
};

// =============================================================================
// WEBHOOK SERVICE
// =============================================================================

export const webhookService = {
  /**
   * Dispatch webhook event
   */
  async dispatch(
    config: WebhookIntegrationConfig,
    event: string,
    payload: Record<string, unknown>
  ): Promise<ApiResponse<boolean>> {
    if (!config.events.includes(event)) {
      return { data: false, error: null };
    }

    try {
      const timestamp = Date.now().toString();
      const body = JSON.stringify({
        event,
        timestamp,
        payload,
      });

      // Create signature for webhook verification
      const signature = await createWebhookSignature(body, config.secret);

      const response = await fetch(config.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Webhook-Signature': signature,
          'X-Webhook-Timestamp': timestamp,
        },
        body,
      });

      if (!response.ok) {
        throw new Error(`Webhook failed: ${response.statusText}`);
      }

      return { data: true, error: null };
    } catch (error) {
      console.error('[Webhook] Dispatch failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Available webhook events
   */
  events: [
    'seed.created',
    'seed.updated',
    'seed.deleted',
    'seed.status_changed',
    'output.created',
    'output.updated',
    'output.deleted',
    'output.status_changed',
    'output.published',
    'note.created',
    'note.mentioned',
    'approval.requested',
    'approval.approved',
    'approval.rejected',
  ],
};

/**
 * Create HMAC signature for webhook verification
 */
async function createWebhookSignature(payload: string, secret: string): Promise<string> {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(secret);
  const payloadData = encoder.encode(payload);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, payloadData);
  return Array.from(new Uint8Array(signature))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// =============================================================================
// CALENDAR EXPORT SERVICE (iCal)
// =============================================================================

export const calendarService = {
  /**
   * Generate iCal file content for content items
   */
  generateICalendar(
    items: Array<{
      id: string;
      title: string;
      description?: string | null;
      date: string;
      type: 'seed' | 'output';
      status: string;
    }>,
    calendarName: string = 'Content Calendar'
  ): string {
    const lines: string[] = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Content Manager//Content Calendar//EN',
      `X-WR-CALNAME:${calendarName}`,
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
    ];

    items.forEach(item => {
      const date = new Date(item.date);
      const dateStr = formatICalDate(date);
      const uid = `${item.id}@contentmanager`;

      lines.push(
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${formatICalDate(new Date())}`,
        `DTSTART;VALUE=DATE:${dateStr.split('T')[0].replace(/-/g, '')}`,
        `SUMMARY:${escapeICalText(item.title)}`,
        `DESCRIPTION:${escapeICalText(item.description || '')}\\nStatus: ${item.status}\\nType: ${item.type}`,
        `CATEGORIES:${item.type.toUpperCase()}`,
        `STATUS:${item.status === 'Published' ? 'COMPLETED' : 'TENTATIVE'}`,
        'END:VEVENT'
      );
    });

    lines.push('END:VCALENDAR');
    return lines.join('\r\n');
  },

  /**
   * Download iCal file
   */
  downloadCalendar(
    items: Array<{
      id: string;
      title: string;
      description?: string | null;
      date: string;
      type: 'seed' | 'output';
      status: string;
    }>,
    filename: string = 'content-calendar.ics'
  ): void {
    const content = this.generateICalendar(items);
    const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /**
   * Get calendar subscription URL (for external calendar apps)
   */
  getSubscriptionUrl(baseUrl: string, userId: string): string {
    return `${baseUrl}/api/calendar/${userId}/feed.ics`;
  },
};

/**
 * Format date for iCal
 */
function formatICalDate(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

/**
 * Escape text for iCal
 */
function escapeICalText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

// =============================================================================
// EMAIL NOTIFICATION SERVICE
// =============================================================================

export const emailService = {
  /**
   * Send email notification (via backend API)
   */
  async sendNotification(
    config: EmailIntegrationConfig,
    to: string,
    subject: string,
    body: string,
    html?: string
  ): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.post<{ success: boolean }>('/notifications/email', {
        from: config.fromAddress,
        to,
        subject,
        body,
        html,
      });

      return { data: response.data?.success ?? false, error: response.error };
    } catch (error) {
      console.error('[Email] Send failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Send mention notification email
   */
  async notifyMention(
    config: EmailIntegrationConfig,
    recipientEmail: string,
    mentionedBy: string,
    contentTitle: string,
    noteText: string,
    contentUrl: string
  ): Promise<ApiResponse<boolean>> {
    if (!config.notifyOnMention) {
      return { data: false, error: null };
    }

    const subject = `${mentionedBy} mentioned you in "${contentTitle}"`;
    const body = `
${mentionedBy} mentioned you in a comment:

"${noteText}"

View the content: ${contentUrl}
    `.trim();

    const html = `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h2 style="color: #1f2937;">You were mentioned</h2>
  <p><strong>${mentionedBy}</strong> mentioned you in a comment on <strong>${contentTitle}</strong>:</p>
  <blockquote style="border-left: 3px solid #8b5cf6; padding-left: 16px; margin: 16px 0; color: #4b5563;">
    ${noteText}
  </blockquote>
  <a href="${contentUrl}" style="display: inline-block; padding: 12px 24px; background: #3b82f6; color: white; text-decoration: none; border-radius: 6px;">
    View Content
  </a>
</div>
    `.trim();

    return this.sendNotification(config, recipientEmail, subject, body, html);
  },

  /**
   * Send assignment notification email
   */
  async notifyAssignment(
    config: EmailIntegrationConfig,
    recipientEmail: string,
    content: ContentOutput,
    assignedBy: string,
    contentUrl: string
  ): Promise<ApiResponse<boolean>> {
    if (!config.notifyOnAssignment) {
      return { data: false, error: null };
    }

    const subject = `You've been assigned: ${content.title}`;
    const dueDate = content.dueDate ? new Date(content.dueDate).toLocaleDateString('en-GB') : 'Not set';

    const body = `
${assignedBy} assigned you to work on "${content.title}".

Content Type: ${content.contentType}
Due Date: ${dueDate}
Status: ${content.status}

View the content: ${contentUrl}
    `.trim();

    return this.sendNotification(config, recipientEmail, subject, body);
  },
};

// =============================================================================
// LINKEDIN MARKETING API SERVICE
// =============================================================================

/**
 * LinkedIn API configuration
 */
const LINKEDIN_OAUTH_BASE = 'https://www.linkedin.com/oauth/v2';

/**
 * LinkedIn Marketing API Service
 * Handles OAuth, publishing, and analytics sync with LinkedIn
 */
// LinkedIn OAuth scope presets
const LINKEDIN_SCOPES = {
  // Community Management API - basic organization posting and engagement metrics
  community: ['w_organization_social', 'r_organization_social'],
  // Marketing Developer Platform - adds full analytics (impressions, clicks, shares)
  marketing: ['w_organization_social', 'r_organization_social', 'r_organization_admin', 'rw_organization_admin'],
};

export const linkedInService = {
  /**
   * Get OAuth scopes based on API access level
   * @param includeMarketing - Set to true if you have Marketing Developer Platform access
   */
  getScopes(includeMarketing: boolean = false): string[] {
    return includeMarketing ? LINKEDIN_SCOPES.marketing : LINKEDIN_SCOPES.community;
  },

  /**
   * Get LinkedIn API configuration status
   * Returns available capabilities based on server configuration
   */
  async getStatus(): Promise<ApiResponse<{
    configured: boolean;
    marketingApiEnabled: boolean;
    capabilities: {
      organizationPosting: boolean;
      personalPosting: boolean;
      metrics: {
        likes: boolean;
        comments: boolean;
        impressions: boolean;
        clicks: boolean;
        shares: boolean;
        engagementRate: boolean;
      };
    };
    scopes: string[];
  }>> {
    try {
      const response = await apiClient.get<{
        configured: boolean;
        marketingApiEnabled: boolean;
        capabilities: {
          organizationPosting: boolean;
          personalPosting: boolean;
          metrics: {
            likes: boolean;
            comments: boolean;
            impressions: boolean;
            clicks: boolean;
            shares: boolean;
            engagementRate: boolean;
          };
        };
        scopes: string[];
      }>('/linkedin/status');
      return response;
    } catch (error) {
      console.error('[LinkedIn] Get status failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get OAuth authorization URL
   * Redirects user to LinkedIn for authentication
   */
  getAuthorizationUrl(
    clientId: string,
    redirectUri: string,
    state: string,
    scopes: string[] = LINKEDIN_SCOPES.community
  ): string {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: redirectUri,
      state,
      scope: scopes.join(' '),
    });
    return `${LINKEDIN_OAUTH_BASE}/authorization?${params.toString()}`;
  },

  /**
   * Exchange authorization code for access token
   * Called from backend to securely handle client secret
   */
  async exchangeCodeForToken(
    code: string,
    redirectUri: string
  ): Promise<ApiResponse<LinkedInTokenResponse>> {
    try {
      const response = await apiClient.post<LinkedInTokenResponse>('/linkedin/oauth/token', {
        code,
        redirectUri,
      });
      return response;
    } catch (error) {
      console.error('[LinkedIn] Token exchange failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Refresh access token before expiration
   */
  async refreshToken(refreshToken: string): Promise<ApiResponse<LinkedInTokenResponse>> {
    try {
      const response = await apiClient.post<LinkedInTokenResponse>('/linkedin/oauth/refresh', {
        refreshToken,
      });
      return response;
    } catch (error) {
      console.error('[LinkedIn] Token refresh failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Validate if the current token is still valid
   */
  async validateToken(config: LinkedInIntegrationConfig): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.post<{ valid: boolean }>('/linkedin/oauth/validate', {
        accessToken: config.accessToken,
      });
      return { data: response.data?.valid ?? false, error: response.error };
    } catch (error) {
      console.error('[LinkedIn] Token validation failed:', error);
      return { data: false, error: error as Error };
    }
  },

  /**
   * Get authenticated user's profile
   */
  async getCurrentUser(config: LinkedInIntegrationConfig): Promise<ApiResponse<LinkedInAuthor>> {
    try {
      const response = await apiClient.get<LinkedInAuthor>('/linkedin/me', {
        headers: { 'X-LinkedIn-Token': config.accessToken },
      });
      return response;
    } catch (error) {
      console.error('[LinkedIn] Get current user failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get organizations the user can post on behalf of
   */
  async getOrganizations(config: LinkedInIntegrationConfig): Promise<ApiResponse<LinkedInOrganization[]>> {
    try {
      const response = await apiClient.get<LinkedInOrganization[]>('/linkedin/organizations', {
        headers: { 'X-LinkedIn-Token': config.accessToken },
      });
      return response;
    } catch (error) {
      console.error('[LinkedIn] Get organizations failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Publish content to LinkedIn
   */
  async publishPost(
    config: LinkedInIntegrationConfig,
    request: LinkedInPostRequest
  ): Promise<ApiResponse<LinkedInPost>> {
    try {
      const response = await apiClient.post<LinkedInPost>('/linkedin/posts', {
        ...request,
        organizationId: request.organizationId || config.organizationId,
        authorId: request.authorId || config.authorId,
        visibility: request.visibility || config.defaultVisibility,
      });
      return response;
    } catch (error) {
      console.error('[LinkedIn] Publish post failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Schedule a post for future publishing
   */
  async schedulePost(
    config: LinkedInIntegrationConfig,
    request: LinkedInPostRequest,
    scheduledFor: string
  ): Promise<ApiResponse<LinkedInPost>> {
    try {
      const response = await apiClient.post<LinkedInPost>('/linkedin/posts/schedule', {
        ...request,
        scheduledFor,
        organizationId: request.organizationId || config.organizationId,
        authorId: request.authorId || config.authorId,
        visibility: request.visibility || config.defaultVisibility,
      });
      return response;
    } catch (error) {
      console.error('[LinkedIn] Schedule post failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Delete a LinkedIn post
   */
  async deletePost(postId: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.delete<{ success: boolean }>(`/linkedin/posts/${postId}`);
      return { data: response.data?.success ?? false, error: response.error };
    } catch (error) {
      console.error('[LinkedIn] Delete post failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get posts for an output
   */
  async getPostsByOutput(outputId: string): Promise<ApiResponse<LinkedInPost[]>> {
    try {
      const response = await apiClient.get<LinkedInPost[]>(`/linkedin/posts?outputId=${outputId}`);
      return response;
    } catch (error) {
      console.error('[LinkedIn] Get posts failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get a single post by ID
   */
  async getPost(postId: string): Promise<ApiResponse<LinkedInPost>> {
    try {
      const response = await apiClient.get<LinkedInPost>(`/linkedin/posts/${postId}`);
      return response;
    } catch (error) {
      console.error('[LinkedIn] Get post failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Sync metrics for a specific post
   */
  async syncPostMetrics(postId: string): Promise<ApiResponse<LinkedInPostMetrics>> {
    try {
      const response = await apiClient.post<LinkedInPostMetrics>(`/linkedin/posts/${postId}/sync-metrics`, {});
      return response;
    } catch (error) {
      console.error('[LinkedIn] Sync post metrics failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Sync metrics for all posts in an organization
   */
  async syncAllMetrics(config: LinkedInIntegrationConfig): Promise<ApiResponse<{ synced: number; failed: number }>> {
    try {
      const response = await apiClient.post<{ synced: number; failed: number }>('/linkedin/sync-metrics', {
        organizationId: config.organizationId,
      });
      return response;
    } catch (error) {
      console.error('[LinkedIn] Sync all metrics failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get analytics summary for the organization
   */
  async getAnalyticsSummary(
    config: LinkedInIntegrationConfig,
    dateRange?: { start: string; end: string }
  ): Promise<ApiResponse<LinkedInAnalyticsSummary>> {
    try {
      let url = `/linkedin/analytics?organizationId=${config.organizationId}`;
      if (dateRange) {
        url += `&startDate=${dateRange.start}&endDate=${dateRange.end}`;
      }
      const response = await apiClient.get<LinkedInAnalyticsSummary>(url);
      return response;
    } catch (error) {
      console.error('[LinkedIn] Get analytics summary failed:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Publish content directly from an output (convenience method)
   * Automatically extracts socialCopy, title, and other fields
   */
  async publishFromOutput(
    config: LinkedInIntegrationConfig,
    output: ContentOutput,
    options?: {
      visibility?: 'PUBLIC' | 'CONNECTIONS' | 'LOGGED_IN';
      scheduledFor?: string;
    }
  ): Promise<ApiResponse<LinkedInPost>> {
    // Prepare content from output
    const content = output.socialCopy || output.excerpt || output.title;
    if (!content) {
      return { data: null, error: new Error('No content available for publishing. Please add social copy or excerpt.') };
    }

    const request: LinkedInPostRequest = {
      outputId: output.id,
      content,
      title: output.title,
      imageUrl: output.featuredImage || undefined,
      visibility: options?.visibility || config.defaultVisibility,
      scheduledFor: options?.scheduledFor,
    };

    if (options?.scheduledFor) {
      return this.schedulePost(config, request, options.scheduledFor);
    }

    return this.publishPost(config, request);
  },

  /**
   * Check if token needs refresh (expires within 1 hour)
   */
  isTokenExpiringSoon(config: LinkedInIntegrationConfig): boolean {
    if (!config.tokenExpiresAt) return false;
    const oneHourFromNow = Date.now() + (60 * 60 * 1000);
    return config.tokenExpiresAt < oneHourFromNow;
  },

  /**
   * Notify about successful publish via other integrations
   */
  async notifyPublishSuccess(
    content: ContentOutput,
    linkedInPost: LinkedInPost,
    notifyConfig?: {
      teamsConfig?: TeamsIntegrationConfig;
    }
  ): Promise<void> {
    const message = `Content "${content.title}" has been published to LinkedIn`;
    const postUrl = `https://www.linkedin.com/feed/update/${linkedInPost.linkedInPostUrn}`;

    if (notifyConfig?.teamsConfig) {
      await teamsService.sendNotification(
        notifyConfig.teamsConfig.webhookUrl,
        '✅ Content Published to LinkedIn',
        message,
        '#0077B5',
        [
          { title: 'Content', value: content.title },
          { title: 'LinkedIn Post URL', value: postUrl },
        ]
      );
    }
  },
};

// =============================================================================
// INTEGRATION MANAGEMENT SERVICE
// =============================================================================

export const integrationService = {
  /**
   * Fetch all integrations
   */
  async fetchAll(): Promise<ApiResponse<IntegrationConfig[]>> {
    return apiClient.get<IntegrationConfig[]>('/integrations');
  },

  /**
   * Fetch integration by ID
   */
  async fetchById(id: string): Promise<ApiResponse<IntegrationConfig>> {
    return apiClient.get<IntegrationConfig>(`/integrations/${id}`);
  },

  /**
   * Create integration
   */
  async create(integration: Omit<IntegrationConfig, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<IntegrationConfig>> {
    return apiClient.post<IntegrationConfig>('/integrations', integration);
  },

  /**
   * Update integration
   */
  async update(id: string, updates: Partial<IntegrationConfig>): Promise<ApiResponse<IntegrationConfig>> {
    return apiClient.patch<IntegrationConfig>(`/integrations/${id}`, updates);
  },

  /**
   * Delete integration
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`/integrations/${id}`);
  },

  /**
   * Test integration connection
   */
  async test(id: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiClient.post<{ success: boolean; message: string }>(`/integrations/${id}/test`, {});
  },
};

// =============================================================================
// TEAMS NOTIFICATION HELPER
// =============================================================================

/**
 * Helper service that reads Teams config from localStorage and sends notifications.
 * Use this throughout the app to easily trigger Teams notifications.
 */
export const teamsNotificationHelper = {
  /**
   * Check if Teams notifications are configured
   */
  isConfigured(): boolean {
    return Boolean(localStorage.getItem('teams_webhook_url'));
  },

  /**
   * Get the current Teams configuration
   */
  getConfig(): TeamsIntegrationConfig | null {
    const webhookUrl = localStorage.getItem('teams_webhook_url');
    if (!webhookUrl) return null;

    return {
      webhookUrl,
      notifyOnMention: localStorage.getItem('teams_notify_mention') !== 'false',
      notifyOnStatusChange: localStorage.getItem('teams_notify_status') !== 'false',
      notifyOnAssignment: localStorage.getItem('teams_notify_assignment') !== 'false',
      notifyOnApproval: localStorage.getItem('teams_notify_approval') !== 'false',
      enableQuickActions: true,
    };
  },

  /**
   * Send a notification about a content status change
   */
  async notifyStatusChange(
    content: ContentSeed | ContentOutput,
    oldStatus: string,
    newStatus: string,
    changedBy: string,
    contentUrl?: string
  ): Promise<void> {
    const config = this.getConfig();
    if (!config) return;

    await teamsService.notifyStatusChange(config, content, oldStatus, newStatus, changedBy, contentUrl);
  },

  /**
   * Send a notification when a user is mentioned
   */
  async notifyMention(
    mentionedUser: string,
    mentionedBy: string,
    contentTitle: string,
    noteText: string,
    contentUrl?: string
  ): Promise<void> {
    const config = this.getConfig();
    if (!config) {
      console.log('[teamsNotificationHelper] No Teams config found - notification not sent');
      return;
    }

    console.log('[teamsNotificationHelper] Sending mention notification:', {
      mentionedUser,
      mentionedBy,
      contentTitle,
      hasWebhookUrl: !!config.webhookUrl,
      notifyOnMention: config.notifyOnMention
    });

    const result = await teamsService.notifyMention(config, mentionedUser, mentionedBy, contentTitle, noteText, contentUrl);
    console.log('[teamsNotificationHelper] Notification result:', result);
  },

  /**
   * Send a notification when content is assigned
   */
  async notifyAssignment(
    content: ContentOutput,
    assignedTo: string,
    assignedBy: string,
    contentUrl?: string
  ): Promise<void> {
    const config = this.getConfig();
    if (!config) return;

    await teamsService.notifyAssignment(config, content, assignedTo, assignedBy, contentUrl);
  },

  /**
   * Send an approval request notification
   */
  async sendApprovalRequest(
    contentTitle: string,
    requestedBy: string,
    approvalId: string,
    contentUrl: string
  ): Promise<void> {
    const config = this.getConfig();
    if (!config) return;

    await teamsService.sendApprovalRequest(config, contentTitle, requestedBy, approvalId, contentUrl);
  },

  /**
   * Send a generic notification
   */
  async sendNotification(
    title: string,
    message: string,
    color?: string,
    facts?: Array<{ title: string; value: string }>
  ): Promise<void> {
    const config = this.getConfig();
    if (!config) return;

    await teamsService.sendNotification(config.webhookUrl, title, message, color, facts);
  },
};

// =============================================================================
// EXPORT ALL SERVICES
// =============================================================================

const integrationServices = {
  teams: teamsService,
  teamsHelper: teamsNotificationHelper,
  webhook: webhookService,
  calendar: calendarService,
  email: emailService,
  linkedIn: linkedInService,
  integration: integrationService,
};

export default integrationServices;
